import Dialog from '@mui/material/Dialog';
import { Bar } from 'react-chartjs-2';
import Directions from '../Directions';
import { useState, useMemo, useContext, useRef } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import TripRateCalculationViewModel from '../../TripRate/ViewModels/TripRateCalculationViewModel';
import TripRateDay from '../../TripRate/ViewModels/TripRateDay';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid2';
import { APIContext } from '../../Contexts/APIContext';
import { CircularProgress } from '@mui/material';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

interface IProps {
    surveys: TripRateCalculationViewModel[];
    onClose: () => void;
    open: boolean;
}

const generateColor = (index: number, total: number): string => {
    const hue = (360 * index) / Math.max(total, 1); // Prevent division by zero
    return `hsl(${hue}, 65%, 55%)`;
};

const isValidTripRateDay = (item: any): item is TripRateDay => {
    return item && typeof item === 'object' && typeof item.period === 'string';
};

const isValidSurvey = (survey: any): survey is TripRateCalculationViewModel => {
    return (
        survey &&
        typeof survey === 'object' &&
        Array.isArray(survey.arrivals) &&
        Array.isArray(survey.departures) &&
        Array.isArray(survey.totals)
    );
};

const getAllTimePeriods = (
    surveys: TripRateCalculationViewModel[],
    direction: string
): string[] => {
    if (!Array.isArray(surveys) || surveys.length === 0) {
        return [];
    }

    const periodsSet = new Set<string>();

    surveys.forEach(survey => {
        if (!isValidSurvey(survey)) {
            console.warn('Invalid survey data detected:', survey);
            return;
        }

        let data: TripRateDay[] = [];
        switch (direction.toLowerCase()) {
            case 'arrivals':
                data = survey.arrivals || [];
                break;
            case 'departures':
                data = survey.departures || [];
                break;
            default:
                data = survey.totals || [];
                break;
        }

        data.forEach(item => {
            if (isValidTripRateDay(item) && item.period) {
                periodsSet.add(item.period);
            }
        });
    });

    return Array.from(periodsSet).sort();
};

const calculatePercentage = (tripRate: number, totalRate: number): number => {
    const validTripRate = typeof tripRate === 'number' && !isNaN(tripRate) ? tripRate : 0;
    const validTotalRate = typeof totalRate === 'number' && !isNaN(totalRate) ? totalRate : 0;

    return validTotalRate > 0 ? (validTripRate / validTotalRate) * 100 : 0;
};

export default function FarTooBigChart({ surveys, onClose, open }: IProps) {
    const [direction, setDirection] = useState<string>('Total');
    const { loading } = useContext(APIContext);
    const chartRef = useRef<any>(null);

    const chartData = useMemo(() => {
        if (!Array.isArray(surveys) || surveys.length === 0) {
            console.warn('No valid surveys data provided');
            return {
                labels: [],
                datasets: [],
                title: 'No Data Available',
            };
        }

        const validSurveys = surveys.filter(isValidSurvey);

        if (validSurveys.length === 0) {
            console.warn('No valid surveys found after filtering');
            return {
                labels: [],
                datasets: [],
                title: 'No Valid Data Available',
            };
        }

        const allPeriods = getAllTimePeriods(validSurveys, direction);

        if (allPeriods.length === 0) {
            return {
                labels: [],
                datasets: [],
                title: 'No Time Periods Available',
            };
        }

        const datasets = validSurveys.map((survey, index) => {
            let selectedData: TripRateDay[] = [];
            let surveyLabel: string = survey.vehicleName || `Survey ${index + 1}`;

            try {
                switch (direction.toLowerCase()) {
                    case 'arrivals':
                        selectedData = Array.isArray(survey.arrivals) ? survey.arrivals : [];
                        break;
                    case 'departures':
                        selectedData = Array.isArray(survey.departures) ? survey.departures : [];
                        break;
                    case 'total':
                    default:
                        selectedData = Array.isArray(survey.totals) ? survey.totals : [];
                        break;
                }

                const validSelectedData = selectedData.filter(isValidTripRateDay);

                const calculatedTotalRate = validSelectedData.reduce((sum, item) => {
                    const tripRate = typeof item.tripRate === 'number' ? item.tripRate : 0;
                    return sum + tripRate;
                }, 0);

                const dataMap = new Map<string, TripRateDay>();
                validSelectedData.forEach(item => {
                    if (item.period) {
                        dataMap.set(item.period, item);
                    }
                });

                const mappedData = allPeriods.map(period => {
                    const item = dataMap.get(period);
                    if (!item) return 0;
                    const tripRate = typeof item.tripRate === 'number' ? item.tripRate : 0;
                    return calculatePercentage(tripRate, calculatedTotalRate);
                });

                const color = generateColor(index, validSurveys.length);

                return {
                    label: surveyLabel,
                    data: mappedData,
                    backgroundColor: color,
                    borderColor: color,
                    borderWidth: 1,
                    barThickness: 'flex' as const,
                    maxBarThickness: 20,
                    rawData: validSelectedData,
                    totalRate: calculatedTotalRate,
                };
            } catch (error) {
                return {
                    label: `Survey ${index + 1} (Error)`,
                    data: new Array(allPeriods.length).fill(0),
                    backgroundColor: '#cccccc',
                    borderColor: '#cccccc',
                    borderWidth: 1,
                    barThickness: 'flex' as const,
                    maxBarThickness: 20,
                    rawData: [],
                    totalRate: 0,
                };
            }
        });

        const chartTitle = `TRIP RATE GRAPH - ${direction.toUpperCase()} - MULTIPLE SURVEYS`;

        return {
            labels: allPeriods,
            datasets: datasets,
            title: chartTitle,
        };
    }, [direction, surveys]);

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                position: 'top' as const,
            },
            title: {
                display: true,
                text: chartData.title,
                font: {
                    size: 14,
                    weight: 'bold' as const,
                },
                padding: 10,
            },
            tooltip: {
                callbacks: {
                    label: (context: any) => {
                        try {
                            const datasetIndex = context.datasetIndex;
                            const dataIndex = context.dataIndex;
                            if (
                                datasetIndex < 0 ||
                                datasetIndex >= chartData.datasets.length ||
                                dataIndex < 0 ||
                                dataIndex >= chartData.labels.length
                            ) {
                                return 'Invalid data';
                            }

                            const dataset = chartData.datasets[datasetIndex];
                            const period = chartData.labels[dataIndex];

                            if (!dataset || !period) {
                                return 'No data available';
                            }

                            const rawDataItem = Array.isArray(dataset.rawData)
                                ? dataset.rawData.find((item: TripRateDay) =>
                                    item && item.period === period
                                )
                                : null;

                            const percentage = context.parsed?.y || 0;
                            const numberOfDays = rawDataItem?.numberOfDays || 0;
                            const tripRate = rawDataItem?.tripRate || 0;

                            return [
                                `${dataset.label || 'Unknown Survey'}`,
                                `Days: ${numberOfDays}`,
                                `Trip Rate: ${tripRate}`,
                                `Percentage: ${percentage.toFixed(1)}%`,
                            ];
                        } catch (error) {
                            console.error('Error in tooltip callback:', error);
                            return 'Error displaying data';
                        }
                    },
                },
            },
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: 'Time Periods',
                    font: {
                        size: 12,
                        weight: 'bold' as const,
                    },
                },
                ticks: {
                    maxRotation: 45,
                    minRotation: 45,
                    font: {
                        size: 10,
                    },
                },
                grid: {
                    display: true,
                    color: '#e0e0e0',
                },
            },
            y: {
                title: {
                    display: true,
                    text: 'Percentage',
                    font: {
                        size: 12,
                        weight: 'bold' as const,
                    },
                },
                beginAtZero: true,
                ticks: {
                    font: {
                        size: 10,
                    },
                    callback: function (value: any) {
                        const numValue = typeof value === 'number' ? value : 0;
                        return numValue.toFixed(1) + '%';
                    },
                },
                grid: {
                    display: true,
                    color: '#e0e0e0',
                },
            },
        },
        layout: {
            padding: {
                top: 10,
                right: 10,
                bottom: 10,
                left: 10,
            },
        },
    };

    const exportChartToJPEG = () => {
        try {
            if (!chartRef.current) {
                console.error('Chart reference not found.');
                return;
            }

            const chartInstance = chartRef.current;
            const sourceCanvas = chartInstance.canvas as HTMLCanvasElement | undefined;
            if (!sourceCanvas) {
                console.error('Chart canvas not found.');
                return;
            }

            const exportCanvas = document.createElement('canvas');
            exportCanvas.width = sourceCanvas.width;
            exportCanvas.height = sourceCanvas.height;
            const exportCtx = exportCanvas.getContext('2d');
            if (!exportCtx) {
                console.error('Could not create export canvas context.');
                return;
            }

            // JPEG does not support transparency; fill white so transparent pixels are not exported as black.
            exportCtx.fillStyle = '#ffffff';
            exportCtx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
            exportCtx.drawImage(sourceCanvas, 0, 0);

            const base64Image = exportCanvas.toDataURL('image/jpeg', 1.0);

            const link = document.createElement('a');
            link.href = base64Image;
            link.download = `${chartData.title}.jpeg`;
            link.click();
        } catch (error) {
            console.error('Error exporting chart as JPEG:', error);
        }
    };

    if ((!chartData.labels.length || !chartData.datasets.length) && !loading) {
        return (
            <Dialog open={open} fullWidth maxWidth="xl" onClose={onClose}>
                <div
                    style={{
                        padding: '40px',
                        textAlign: 'center',
                        minHeight: '300px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                    }}
                >
                    <h3>No Data Available</h3>
                    <p>Please check your survey data and try again.</p>
                </div>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} fullWidth maxWidth="xl" onClose={onClose}>
            <Grid container>
                <Grid size={10}>
                    <Directions setType={setDirection} type={direction} />
                </Grid>
                <Grid size={2}>
                    <Button onClick={exportChartToJPEG} disabled={loading}>Export</Button>
                </Grid>
            </Grid>
            <div style={{ position: 'relative', height: '80vh' }}>
                {loading ? <CircularProgress/>
                    : <Bar ref={chartRef} data={chartData} options={options} />}
            </div>
        </Dialog>
    );
}
